import React, { useMemo, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { Car, Gamepad2, ListOrdered, Map, MapPin, Navigation, Play, Route, type LucideIcon } from 'lucide-react';
import { useLanguage } from '../../i18n/LanguageContext';
import type { MessageKey } from '../../i18n/en';
import {
  displaySpeed,
  formatPointName,
  isDefaultCategoryId,
  joystickDisplayBounds,
  MAP_THEMES,
  NAME_FORMATS,
  POLYLINE_COLORS,
  ROUTE_COLORS,
  storageSpeedFromDisplay,
  type AppDefaults,
  type DefaultCategoryId,
  type SpeedUnit,
  type TrayHeight,
  type TrayView,
} from './app-defaults-model';
import { SectionLabel, SegmentField, SliderField, SwitchRow } from './DefaultsControls';
import { SettingsDialog, SettingsPath } from './SettingsChrome';
import { SETTINGS_DEFAULTS, SETTINGS_HOME } from './settings-paths';
import { useAppDefaults } from './use-app-defaults';
import './settings-home.css';

const CATEGORY_COPY: Record<DefaultCategoryId, { title: MessageKey; desc: MessageKey }> = {
  map: { title: 'defaults.map.title', desc: 'defaults.map.desc' },
  stationary: { title: 'defaults.stationary.title', desc: 'defaults.stationary.desc' },
  playlist: { title: 'defaults.playlist.title', desc: 'defaults.playlist.desc' },
  itinerary: { title: 'defaults.itinerary.title', desc: 'defaults.itinerary.desc' },
  joystick: { title: 'defaults.joystick.title', desc: 'defaults.joystick.desc' },
  player: { title: 'defaults.player.title', desc: 'defaults.player.desc' },
};

const CATEGORY_ICON: Record<DefaultCategoryId, string> = {
  map: 'map',
  stationary: 'stationary',
  playlist: 'playlist',
  itinerary: 'itinerary',
  joystick: 'joystick',
  player: 'player',
};

const CATEGORY_MARK: Record<DefaultCategoryId, LucideIcon> = {
  map: Map,
  stationary: MapPin,
  playlist: ListOrdered,
  itinerary: Route,
  joystick: Gamepad2,
  player: Play,
};

const AppDefaultCategoryPage: React.FC = () => {
  const { category: raw = '' } = useParams();
  if (!isDefaultCategoryId(raw)) {
    return <Navigate to={SETTINGS_DEFAULTS} replace />;
  }
  return <CategoryScreen category={raw} />;
};

const CategoryScreen: React.FC<{ category: DefaultCategoryId }> = ({ category }) => {
  const { t, locale } = useLanguage();
  const { defaults, update, resetCategory } = useAppDefaults();
  const [formatTarget, setFormatTarget] = useState<'stationary' | 'joystick' | null>(null);
  const [locationNote, setLocationNote] = useState(false);
  const copy = CATEGORY_COPY[category];
  const changed = useMemo(() => !sameAsPhone(category, defaults), [category, defaults]);
  const Mark = CATEGORY_MARK[category];

  return (
    <div className={category === 'itinerary' ? 'set set--itinerary' : 'set'}>
      <SettingsPath
        items={[
          { label: t('settings.back'), to: SETTINGS_HOME },
          { label: t('settings.home.defaults.title'), to: SETTINGS_DEFAULTS },
          { label: t(copy.title) },
        ]}
      />
      <div className="set-pagehead">
        <div className="set-pagehead__titles">
          <span className={`set-ico set-ico--${CATEGORY_ICON[category]}`}>
            <Mark size={20} aria-hidden="true" />
          </span>
          <div>
            <h2>{t(copy.title)}</h2>
            <p className="set-card__lead">{t(copy.desc)}</p>
          </div>
        </div>
        <button
          type="button"
          className="set-textbtn"
          onClick={() => {
            resetCategory(category);
            setLocationNote(false);
          }}
          disabled={!changed}
        >
          {t('defaults.reset')}
        </button>
      </div>

      {category === 'map' && (
        <>
          <section className="set-card">
            <SectionLabel>{t('defaults.section.location')}</SectionLabel>
            <div>
              <h3>{t('defaults.actual.title')}</h3>
              <p className="set-card__lead">{t('defaults.actual.desc')}</p>
            </div>
            <div className="set-empty">
              <strong>{t('defaults.actual.empty')}</strong>
              <p>{t('defaults.actual.emptyHint')}</p>
            </div>
            {locationNote ? <p className="set-card__lead">{t('defaults.actual.pending')}</p> : null}
            <button type="button" className="set-textbtn" onClick={() => setLocationNote(true)}>
              {t('defaults.actual.refresh')}
            </button>
          </section>
          <section className="set-card">
            <SectionLabel>{t('defaults.section.map')}</SectionLabel>
            <div>
              <h3>{t('defaults.mapTheme.title')}</h3>
              <p className="set-card__lead">{t('defaults.mapTheme.desc')}</p>
            </div>
            <div className="set-themes" role="group" aria-label={t('defaults.mapTheme.group')}>
              {MAP_THEMES.map((theme) => (
                <button
                  key={theme.id}
                  type="button"
                  className="set-themeopt"
                  aria-pressed={defaults.mapTheme === theme.id}
                  onClick={() => update({ mapTheme: theme.id })}
                >
                  <span className={`set-map set-map--${theme.id}`} aria-hidden="true">
                    <span />
                    <span />
                  </span>
                  <span>
                    <strong>{t(theme.labelKey)}</strong>
                    <small>{t(theme.descKey)}</small>
                  </span>
                </button>
              ))}
            </div>
          </section>
          <section className="set-card">
            <SectionLabel>{t('defaults.section.camera')}</SectionLabel>
            <SliderField
              title={t('defaults.zoom.title')}
              description={t('defaults.zoom.desc')}
              valueLabel={String(defaults.mapZoom)}
              min={10}
              max={20}
              value={defaults.mapZoom}
              minLabel={t('defaults.range.zoomMin')}
              maxLabel={t('defaults.range.zoomMax')}
              onChange={(mapZoom) => update({ mapZoom })}
            />
            <div className="set-split" />
            <SliderField
              title={t('defaults.tilt.title')}
              description={t('defaults.tilt.desc')}
              valueLabel={t('defaults.degrees', { n: defaults.mapTilt })}
              min={0}
              max={65}
              value={defaults.mapTilt}
              minLabel={t('defaults.range.tiltMin')}
              maxLabel={t('defaults.range.tiltMax')}
              onChange={(mapTilt) => update({ mapTilt })}
            />
          </section>
        </>
      )}

      {category === 'stationary' && (
        <section className="set-card">
          <SectionLabel>{t('defaults.section.history')}</SectionLabel>
          <SliderField
            title={t('defaults.accuracy.title')}
            description={t('defaults.accuracy.desc')}
            valueLabel={t('defaults.meters', { n: defaults.stationaryAccuracy })}
            min={0}
            max={300}
            value={defaults.stationaryAccuracy}
            minLabel={t('defaults.range.accuracyMin')}
            maxLabel={t('defaults.range.accuracyMax')}
            onChange={(stationaryAccuracy) => update({ stationaryAccuracy })}
          />
          <div className="set-split" />
          <NameFormatRow
            title={t('defaults.nameFormat.title')}
            description={t('defaults.nameFormat.desc')}
            value={formatPointName(defaults.stationaryNameFormat, new Date(), locale)}
            onOpen={() => setFormatTarget('stationary')}
          />
          <div className="set-split" />
          <SectionLabel>{t('defaults.section.search')}</SectionLabel>
          <SliderField
            title={t('defaults.search.title')}
            description={t('defaults.search.stationary')}
            valueLabel={t('defaults.kilometers', { n: defaults.stationarySearchKm })}
            min={3}
            max={50}
            value={defaults.stationarySearchKm}
            minLabel={t('defaults.range.searchMin')}
            maxLabel={t('defaults.range.searchMax')}
            onChange={(stationarySearchKm) => update({ stationarySearchKm })}
          />
          <div className="set-split" />
          <SectionLabel>{t('defaults.section.view')}</SectionLabel>
          <SegmentField<TrayHeight>
            label={t('defaults.tray.title')}
            description={t('defaults.tray.stationaryDesc')}
            groupLabel={t('defaults.tray.group')}
            value={defaults.stationaryTray}
            options={[
              { id: 'SMALL', label: t('defaults.tray.small') },
              { id: 'MEDIUM', label: t('defaults.tray.medium') },
              { id: 'LARGE', label: t('defaults.tray.large') },
            ]}
            onChange={(stationaryTray) => update({ stationaryTray })}
          />
          <SegmentField<TrayView>
            label={t('defaults.view.historyTitle')}
            description={t('defaults.view.historyDesc')}
            groupLabel={t('defaults.view.group')}
            value={defaults.stationaryView}
            options={[
              { id: 'LIST', label: t('defaults.view.list') },
              { id: 'GRID', label: t('defaults.view.grid') },
            ]}
            onChange={(stationaryView) => update({ stationaryView })}
          />
        </section>
      )}

      {category === 'playlist' && (
        <section className="set-card">
          <SectionLabel>{t('defaults.section.playlist')}</SectionLabel>
          <SliderField
            title={t('defaults.time.title')}
            description={t('defaults.time.desc')}
            valueLabel={t('defaults.seconds', { n: defaults.playlistTimeSec })}
            min={0}
            max={600}
            value={defaults.playlistTimeSec}
            minLabel={t('defaults.range.timeMin')}
            maxLabel={t('defaults.range.timeMax')}
            onChange={(playlistTimeSec) => update({ playlistTimeSec })}
          />
          <div className="set-split" />
          <SliderField
            title={t('defaults.plAccuracy.title')}
            description={t('defaults.plAccuracy.desc')}
            valueLabel={t('defaults.meters', { n: defaults.playlistAccuracy })}
            min={0}
            max={500}
            value={defaults.playlistAccuracy}
            minLabel={t('defaults.range.plAccuracyMin')}
            maxLabel={t('defaults.range.plAccuracyMax')}
            onChange={(playlistAccuracy) => update({ playlistAccuracy })}
          />
          <div className="set-split" />
          <SectionLabel>{t('defaults.section.search')}</SectionLabel>
          <SliderField
            title={t('defaults.search.title')}
            description={t('defaults.search.playlist')}
            valueLabel={t('defaults.kilometers', { n: defaults.playlistSearchKm })}
            min={3}
            max={50}
            value={defaults.playlistSearchKm}
            minLabel={t('defaults.range.searchMin')}
            maxLabel={t('defaults.range.searchMax')}
            onChange={(playlistSearchKm) => update({ playlistSearchKm })}
          />
          <div className="set-split" />
          <SectionLabel>{t('defaults.section.view')}</SectionLabel>
          <SegmentField<TrayHeight>
            label={t('defaults.tray.title')}
            description={t('defaults.tray.playlistDesc')}
            groupLabel={t('defaults.tray.group')}
            value={defaults.playlistTray}
            options={[
              { id: 'SMALL', label: t('defaults.tray.small') },
              { id: 'MEDIUM', label: t('defaults.tray.medium') },
              { id: 'LARGE', label: t('defaults.tray.large') },
            ]}
            onChange={(playlistTray) => update({ playlistTray })}
          />
          <SegmentField<TrayView>
            label={t('defaults.view.playlistTitle')}
            description={t('defaults.view.playlistDesc')}
            groupLabel={t('defaults.view.group')}
            value={defaults.playlistView}
            options={[
              { id: 'LIST', label: t('defaults.view.list') },
              { id: 'GRID', label: t('defaults.view.grid') },
            ]}
            onChange={(playlistView) => update({ playlistView })}
          />
        </section>
      )}

      {category === 'itinerary' && (
        <section className="set-card">
          <SectionLabel>{t('defaults.section.motion')}</SectionLabel>
          <SliderField
            title={t('defaults.speed.title')}
            description={t('defaults.speed.desc')}
            valueLabel={t('defaults.speedValue', {
              n: displaySpeed(defaults.itinerarySpeedKmh, defaults.itinerarySpeedUnit),
              unit: t(defaults.itinerarySpeedUnit === 'mph' ? 'defaults.unit.mph' : 'defaults.unit.kmh'),
            })}
            min={1}
            max={300}
            value={defaults.itinerarySpeedKmh}
            minLabel={t('defaults.speedValue', {
              n: displaySpeed(1, defaults.itinerarySpeedUnit),
              unit: t(defaults.itinerarySpeedUnit === 'mph' ? 'defaults.unit.mph' : 'defaults.unit.kmh'),
            })}
            maxLabel={t('defaults.speedValue', {
              n: displaySpeed(300, defaults.itinerarySpeedUnit),
              unit: t(defaults.itinerarySpeedUnit === 'mph' ? 'defaults.unit.mph' : 'defaults.unit.kmh'),
            })}
            onChange={(itinerarySpeedKmh) => update({ itinerarySpeedKmh })}
          />
          <SegmentField<SpeedUnit>
            label={t('defaults.unit.title')}
            description={t('defaults.unit.itineraryDesc')}
            groupLabel={t('defaults.unit.group')}
            value={defaults.itinerarySpeedUnit}
            options={[
              { id: 'kmh', label: t('defaults.unit.kmh') },
              { id: 'mph', label: t('defaults.unit.mph') },
            ]}
            onChange={(itinerarySpeedUnit) => update({ itinerarySpeedUnit })}
          />
          <div className="set-split" />
          <SliderField
            title={t('defaults.itinAccuracy.title')}
            description={t('defaults.itinAccuracy.desc')}
            valueLabel={t('defaults.meters', { n: defaults.itineraryAccuracy })}
            min={1}
            max={100}
            value={defaults.itineraryAccuracy}
            minLabel={t('defaults.range.itinAccuracyMin')}
            maxLabel={t('defaults.range.itinAccuracyMax')}
            onChange={(itineraryAccuracy) => update({ itineraryAccuracy })}
          />
          <div className="set-split" />
          <SectionLabel>{t('defaults.section.search')}</SectionLabel>
          <SliderField
            title={t('defaults.search.title')}
            description={t('defaults.search.itinerary')}
            valueLabel={t('defaults.kilometers', { n: defaults.itinerarySearchKm })}
            min={3}
            max={50}
            value={defaults.itinerarySearchKm}
            minLabel={t('defaults.range.searchMin')}
            maxLabel={t('defaults.range.searchMax')}
            onChange={(itinerarySearchKm) => update({ itinerarySearchKm })}
          />
          <div className="set-split" />
          <SectionLabel>{t('defaults.section.style')}</SectionLabel>
          <div>
            <h3>{t('defaults.color.title')}</h3>
            <p className="set-card__lead">{t('defaults.color.desc')}</p>
          </div>
          <div className="set-swatches" role="group" aria-label={t('defaults.color.group')}>
            {ROUTE_COLORS.map((color) => (
              <button
                key={color}
                type="button"
                className="set-swatch"
                style={{ background: color }}
                aria-label={color}
                aria-pressed={defaults.itineraryColor.toLowerCase() === color.toLowerCase()}
                onClick={() => update({ itineraryColor: color })}
              />
            ))}
          </div>
        </section>
      )}

      {category === 'joystick' && (
        <>
          <section className="set-card">
            <SectionLabel>{t('defaults.section.motion')}</SectionLabel>
            <SegmentField<SpeedUnit>
              label={t('defaults.unit.title')}
              description={t('defaults.joyUnit.desc')}
              groupLabel={t('defaults.unit.group')}
              value={defaults.joystickSpeedUnit}
              options={[
                { id: 'kmh', label: t('defaults.unit.kmh') },
                { id: 'mph', label: t('defaults.unit.mph') },
              ]}
              onChange={(joystickSpeedUnit) => update({ joystickSpeedUnit })}
            />
            <JoystickSpeed
              unit={defaults.joystickSpeedUnit}
              kmh={defaults.joystickSpeedKmh}
              onChange={(joystickSpeedKmh) => update({ joystickSpeedKmh })}
            />
            <div className="set-split" />
            <SliderField
              title={t('defaults.joyAccuracy.title')}
              description={t('defaults.joyAccuracy.desc')}
              valueLabel={t('defaults.meters', { n: defaults.joystickAccuracy })}
              min={1}
              max={100}
              value={defaults.joystickAccuracy}
              minLabel={t('defaults.range.joyAccuracyMin')}
              maxLabel={t('defaults.range.joyAccuracyMax')}
              onChange={(joystickAccuracy) => update({ joystickAccuracy })}
            />
            <div className="set-split" />
            <NameFormatRow
              title={t('defaults.joyName.title')}
              description={t('defaults.joyName.desc')}
              value={formatPointName(defaults.joystickNameFormat, new Date(), locale)}
              onOpen={() => setFormatTarget('joystick')}
            />
          </section>
          <section className="set-card">
            <SectionLabel>{t('defaults.section.appearance')}</SectionLabel>
            <div>
              <h3>{t('defaults.marker.title')}</h3>
              <p className="set-card__lead">{t('defaults.marker.desc')}</p>
            </div>
            <div className="set-markers" role="group" aria-label={t('defaults.marker.group')}>
              {(
                [
                  ['GOOGLE', 'defaults.marker.maps', MapPin],
                  ['CAR', 'defaults.marker.car', Car],
                  ['WAZE', 'defaults.marker.waze', Navigation],
                ] as const
              ).map(([id, label, Icon]) => (
                <button
                  key={id}
                  type="button"
                  className="set-marker"
                  aria-pressed={defaults.joystickMarker === id}
                  onClick={() => update({ joystickMarker: id })}
                >
                  <Icon size={22} aria-hidden="true" />
                  <strong>{t(label)}</strong>
                </button>
              ))}
            </div>
            <div className="set-split" />
            <div>
              <h3>{t('defaults.polyline.title')}</h3>
              <p className="set-card__lead">{t('defaults.polyline.desc')}</p>
            </div>
            <div className="set-swatches" role="group" aria-label={t('defaults.polyline.group')}>
              {POLYLINE_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  className="set-swatch"
                  style={{ background: color }}
                  aria-label={color}
                  aria-pressed={defaults.joystickPolyline.toLowerCase() === color.toLowerCase()}
                  onClick={() => update({ joystickPolyline: color })}
                />
              ))}
            </div>
          </section>
        </>
      )}

      {category === 'player' && (
        <section className="set-card">
          <SectionLabel>{t('defaults.section.overlay')}</SectionLabel>
          <SwitchRow
            title={t('defaults.overlay.stationary.title')}
            description={t('defaults.overlay.stationary.desc')}
            checked={defaults.overlayStationary}
            onChange={(overlayStationary) => update({ overlayStationary })}
          />
          <div className="set-split" />
          <SwitchRow
            title={t('defaults.overlay.playlist.title')}
            description={t('defaults.overlay.playlist.desc')}
            checked={defaults.overlayPlaylist}
            onChange={(overlayPlaylist) => update({ overlayPlaylist })}
          />
          <div className="set-split" />
          <SwitchRow
            title={t('defaults.overlay.itinerary.title')}
            description={t('defaults.overlay.itinerary.desc')}
            checked={defaults.overlayItinerary}
            onChange={(overlayItinerary) => update({ overlayItinerary })}
          />
          <div className="set-split" />
          <SwitchRow
            title={t('defaults.overlay.joystick.title')}
            description={t('defaults.overlay.joystick.desc')}
            checked={defaults.overlayJoystick}
            onChange={(overlayJoystick) => update({ overlayJoystick })}
          />
        </section>
      )}

      {formatTarget && (
        <SettingsDialog
          title={t(formatTarget === 'stationary' ? 'defaults.nameFormat.dialogTitle' : 'defaults.joyName.dialogTitle')}
          subtitle={t('defaults.nameFormat.dialogSubtitle')}
          closeLabel={t('defaults.close')}
          onClose={() => setFormatTarget(null)}
        >
          <div className="set-format" role="radiogroup" aria-label={t('defaults.nameFormat.group')}>
            {NAME_FORMATS.map((format) => {
              const selected =
                (formatTarget === 'stationary' ? defaults.stationaryNameFormat : defaults.joystickNameFormat) ===
                format.id;
              return (
                <button
                  key={format.id}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => {
                    const patch =
                      formatTarget === 'stationary'
                        ? { stationaryNameFormat: format.id }
                        : { joystickNameFormat: format.id };
                    update(patch);
                    setFormatTarget(null);
                  }}
                >
                  <strong>{t(format.labelKey)}</strong>
                  <small>{formatPointName(format.id, new Date(), locale)}</small>
                </button>
              );
            })}
          </div>
        </SettingsDialog>
      )}
    </div>
  );
};

function NameFormatRow({
  title,
  description,
  value,
  onOpen,
}: {
  title: string;
  description: string;
  value: string;
  onOpen: () => void;
}) {
  return (
    <button type="button" className="set-choice" onClick={onOpen}>
      <span>
        <strong>{title}</strong>
        <span className="set-choice__desc">{description}</span>
      </span>
      <small>{value}</small>
    </button>
  );
}

function JoystickSpeed({
  unit,
  kmh,
  onChange,
}: {
  unit: SpeedUnit;
  kmh: number;
  onChange: (kmh: number) => void;
}) {
  const { t } = useLanguage();
  const bounds = joystickDisplayBounds(unit);
  const shown = displaySpeed(kmh, unit);
  return (
    <SliderField
      title={t('defaults.joySpeed.title')}
      description={t('defaults.joySpeed.desc')}
      valueLabel={t('defaults.speedValue', {
        n: shown,
        unit: t(unit === 'mph' ? 'defaults.unit.mph' : 'defaults.unit.kmh'),
      })}
      min={bounds.min}
      max={bounds.max}
      value={Math.min(bounds.max, Math.max(bounds.min, shown))}
      minLabel={String(bounds.min)}
      maxLabel={String(bounds.max)}
      onChange={(display) => onChange(storageSpeedFromDisplay(display, unit))}
    />
  );
}

function sameAsPhone(category: DefaultCategoryId, defaults: AppDefaults): boolean {
  const phone = {
    map: defaults.mapTheme === 'SILVER' && defaults.mapZoom === 14 && defaults.mapTilt === 60,
    stationary:
      defaults.stationaryAccuracy === 25 &&
      defaults.stationaryNameFormat === 'DAY_SHORT_DATE_12H' &&
      defaults.stationarySearchKm === 50 &&
      defaults.stationaryTray === 'SMALL' &&
      defaults.stationaryView === 'LIST',
    playlist:
      defaults.playlistTimeSec === 8 &&
      defaults.playlistAccuracy === 15 &&
      defaults.playlistSearchKm === 50 &&
      defaults.playlistTray === 'MEDIUM' &&
      defaults.playlistView === 'LIST',
    itinerary:
      defaults.itinerarySpeedKmh === 33 &&
      defaults.itinerarySpeedUnit === 'kmh' &&
      defaults.itineraryAccuracy === 28 &&
      defaults.itinerarySearchKm === 50 &&
      defaults.itineraryColor.toLowerCase() === '#333333',
    joystick:
      defaults.joystickSpeedUnit === 'kmh' &&
      defaults.joystickSpeedKmh === 10 &&
      defaults.joystickAccuracy === 5 &&
      defaults.joystickNameFormat === 'DAY_SHORT_DATE_12H' &&
      defaults.joystickMarker === 'GOOGLE' &&
      defaults.joystickPolyline.toLowerCase() === '#7c3aed',
    player:
      defaults.overlayStationary &&
      defaults.overlayPlaylist &&
      defaults.overlayItinerary &&
      defaults.overlayJoystick,
  };
  return phone[category];
}

export default AppDefaultCategoryPage;
